/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SETTINGS OPERATIONS (UPDATED FOR NEW SCHEMA)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Manages invoice settings with:
 * - Single mutable row per user (no versioning)
 * - UPSERT pattern (atomic create/update)
 * - Currency calculation from brand data
 * - Multi-user isolation (RLS)
 * - Data validation (Zod schemas)
 *
 * SCHEMA CHANGE:
 * - OLD: Multiple snapshots per user with version history (immutable)
 * - NEW: One settings record per user (mutable, updated in place)
 *
 * INVOICE SNAPSHOTS:
 * - When invoice is created, settings data is COPIED into invoice as JSONB
 * - This table stores CURRENT settings only
 * - Historical settings live in invoice.settings_snapshot JSONB column
 *
 * SOLVED ISSUES:
 * - Issue #1: ACID Transactions ✓ PostgreSQL UPSERT is atomic
 * - Issue #2: Counter Before Operation ✓ N/A (no version counters)
 * - Issue #3: Multi-Step Not Atomic ✓ Single UPSERT operation
 * - Issue #4: Export Not Atomic ✓ Single row per user (no export needed)
 * - Issue #5: Lock Timeout Race ✓ PostgreSQL handles concurrent UPSERTs
 * - Issue #6: Bulk Partial Failure ✓ N/A (single operation per user)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { supabase, getCurrentUserId } from "../core/supabase.js";
import { getTimestamp } from "../core/utils.js";
import { settingsSnapshotSchema, validateOrThrow } from "../validation/schemas.js";
import { requireFeature} from '../core/permissions.js';
import { FEATURES} from '../features/featureList.js';

// ══════════════════════════════════════════════════════════════════════════════
// SETTINGS OPERATIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Update settings (create or update)
 * Called when user clicks "Save Settings"
 *
 * ✅ ATOMIC OPERATION: Uses PostgreSQL UPSERT (INSERT ... ON CONFLICT DO UPDATE)
 * ✅ MULTI-DEVICE SAFE: Concurrent updates from different devices handled correctly
 * ✅ VALIDATION: Zod schema validation before database write
 * ✅ CURRENCY CALCULATION: Derives currency info from brand data
 *
 * @param {object} settingsData - Settings state from UI
 * @param {object} brandData - Brand state (for currency info)
 * @returns {Promise<object>} Updated settings
 */
export async function updateSettings(settingsData, brandData) {
  // ✅ PERMISSION CHECKS
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  // Check custom fields feature if being configured
  if (settingsData.customField1 || settingsData.customField2) {
    await requireFeature(FEATURES.CUSTOM_FIELDS);
  }
  
  // Check custom invoice numbering if prefix is being changed
  if (settingsData.invPrefix && settingsData.invPrefix !== 'INV') {
    await requireFeature(FEATURES.CUSTOM_INVOICE_NUMBERING);
  }
  
  // ────────────────────────────────────────────────────────────────────────
  // STEP 1: VALIDATE INPUTS
  // ────────────────────────────────────────────────────────────────────────

  if (!settingsData || typeof settingsData !== 'object') {
    throw new Error('settingsData is required');
  }

  if (!brandData || typeof brandData !== 'object') {
    throw new Error('brandData is required (for currency calculation)');
  }

  // ────────────────────────────────────────────────────────────────────────
  // STEP 2: CALCULATE CURRENCY SYMBOL FROM BRAND DATA
  // ────────────────────────────────────────────────────────────────────────

  // Import currencies dynamically
  const { CURRENCIES } = await import("../../utils.js");
  
  const currencySymbol = brandData.currency === "NONE"
    ? ""
    : brandData.currency === "CUSTOM"
      ? (brandData.customCurrencySymbol || "?")
      : (CURRENCIES.find(c => c.value === brandData.currency)?.symbol || "");

  // ────────────────────────────────────────────────────────────────────────
  // STEP 3: BUILD SETTINGS DATA WITH DEFAULTS
  // ────────────────────────────────────────────────────────────────────────

  const settings = {
    // Currency (derived from brand)
    currency: brandData.currency || "NONE",
    currencySymbol,
    customCurrencySymbol: brandData.customCurrencySymbol || "",
    currencyFormat: settingsData.currencyFormat || "symbol-before-space",

    // Amount formatting
    amountGrouping: settingsData.amountGrouping || "international",
    amountSeparator: settingsData.amountSeparator || "comma-dot",
    amountDecimals: settingsData.amountDecimals !== false,

    // Date & Time
    dateFormat: settingsData.dateFormat || "DD-MM-YYYY",
    dateSeparator: settingsData.dateSeparator || "-",
    timeFormat: settingsData.timeFormat || "12hr",

    // Page
    pageSize: settingsData.pageSize || "A4",

    // Workflow
    billingLayout: settingsData.billingLayout || "detailed",
    customerCardCollapsed: settingsData.customerCardCollapsed || false,

    // Display options
    showHSN: settingsData.showHSN || false,
    showProductID: settingsData.showProductID || false,
    showDiscount: settingsData.showDiscount !== false,

    // Custom fields
    customField1: settingsData.customField1 || { enabled: false, name: "", showInInvoice: true },
    customField2: settingsData.customField2 || { enabled: false, name: "", showInInvoice: true },

    // Invoice numbering
    invPrefix: settingsData.invPrefix || "INV",
    invDateFormat: settingsData.invDateFormat || "YYYY-MM-DD",
    invSeparator: settingsData.invSeparator || "-",
    invMinDigits: settingsData.invMinDigits || 4,

    // Notes
    notesSections: settingsData.notesSections || []
  };

  // ────────────────────────────────────────────────────────────────────────
  // STEP 4: VALIDATE AGAINST SCHEMA
  // ────────────────────────────────────────────────────────────────────────

  const validatedData = validateOrThrow(settingsSnapshotSchema, settings);

  // ────────────────────────────────────────────────────────────────────────
  // STEP 5: UPSERT TO DATABASE (ATOMIC)
  // ────────────────────────────────────────────────────────────────────────

  const userId = await getCurrentUserId();

  // Convert to database format (snake_case)
  const dbSettings = {
    user_id: userId,

    // Currency
    currency: validatedData.currency,
    currency_symbol: validatedData.currencySymbol,
    custom_currency_symbol: validatedData.customCurrencySymbol,
    currency_format: validatedData.currencyFormat,

    // Amount formatting
    amount_grouping: validatedData.amountGrouping,
    amount_separator: validatedData.amountSeparator,
    amount_decimals: validatedData.amountDecimals,

    // Date & Time
    date_format: validatedData.dateFormat,
    date_separator: validatedData.dateSeparator,
    time_format: validatedData.timeFormat,

    // Page
    page_size: validatedData.pageSize,

    // Workflow
    billing_layout: validatedData.billingLayout,
    customer_card_collapsed: validatedData.customerCardCollapsed,

    // Display options
    show_hsn: validatedData.showHSN,
    show_product_id: validatedData.showProductID,
    show_discount: validatedData.showDiscount,

    // Custom fields (JSON)
    custom_field1: validatedData.customField1,
    custom_field2: validatedData.customField2,

    // Invoice numbering
    inv_prefix: validatedData.invPrefix,
    inv_date_format: validatedData.invDateFormat,
    inv_separator: validatedData.invSeparator,
    inv_min_digits: validatedData.invMinDigits,

    // Notes (JSON)
    notes_sections: validatedData.notesSections,
    
    // Timestamp
    updated_at: getTimestamp()
  };

  // ✅ ATOMIC UPSERT: If row exists (user_id match), UPDATE; otherwise INSERT
  const { data, error } = await supabase
    .from('settings_snapshots')
    .upsert(dbSettings, { 
      onConflict: 'user_id',  // Primary key
      ignoreDuplicates: false  // Always update if exists
    })
    .select()
    .single();

  if (error) {
    console.error('Failed to update settings:', error);
    throw error;
  }

  console.log(`✓ Settings updated for user ${userId}`);

  // Return in camelCase format
  return convertSettingsToCamelCase(data);
}

/**
 * Get current settings for user
 *
 * @returns {Promise<object|null>} Settings or null if not found
 */
export async function getCurrentSettings() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();

  // ✅ SIMPLE QUERY: One row per user (no joins, no version filtering)
  const { data, error } = await supabase
    .from('settings_snapshots')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('Failed to get settings:', error);
    throw error;
  }

  if (!data) {
    console.debug(`No settings found for user ${userId}`);
    return null;
  }

  return convertSettingsToCamelCase(data);
}

// ══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Convert database row (snake_case) to app format (camelCase)
 * @private
 */
function convertSettingsToCamelCase(settings) {
  if (!settings) return null;

  return {
    userId: settings.user_id,
    updatedAt: settings.updated_at,

    // Currency
    currency: settings.currency,
    currencySymbol: settings.currency_symbol,
    customCurrencySymbol: settings.custom_currency_symbol,
    currencyFormat: settings.currency_format,

    // Amount formatting
    amountGrouping: settings.amount_grouping,
    amountSeparator: settings.amount_separator,
    amountDecimals: settings.amount_decimals,

    // Date & Time
    dateFormat: settings.date_format,
    dateSeparator: settings.date_separator,
    timeFormat: settings.time_format,

    // Page
    pageSize: settings.page_size,

    // Workflow
    billingLayout: settings.billing_layout,
    customerCardCollapsed: settings.customer_card_collapsed,

    // Display options
    showHSN: settings.show_hsn,
    showProductID: settings.show_product_id,
    showDiscount: settings.show_discount,

    // Custom fields (JSON)
    customField1: settings.custom_field1,
    customField2: settings.custom_field2,

    // Invoice numbering
    invPrefix: settings.inv_prefix,
    invDateFormat: settings.inv_date_format,
    invSeparator: settings.inv_separator,
    invMinDigits: settings.inv_min_digits,

    // Notes (JSON)
    notesSections: settings.notes_sections
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY
// ══════════════════════════════════════════════════════════════════════════════

/**
 * EXPORTED FUNCTIONS (2 total):
 *
 * ✅ updateSettings(settingsData, brandData)
 *    - Creates or updates settings for current user
 *    - Calculates currency info from brand data
 *    - Uses UPSERT pattern (atomic)
 *    - Returns updated settings
 *
 * ✅ getCurrentSettings()
 *    - Gets current settings for user
 *    - Returns null if no settings exist
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * REMOVED FUNCTIONS (10 deleted):
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ❌ createSettingsSnapshot() - Replaced by updateSettings()
 * ❌ getSettingsSnapshot() - Replaced by getCurrentSettings()
 * ❌ getLatestSettingsSnapshot() - No longer needed (only one row exists)
 * ❌ getAllSettingsSnapshots() - No snapshots to list
 * ❌ getSettingsSnapshotsByVersionRange() - No versions
 * ❌ getSettingsSnapshotCount() - Always 1 or 0
 * ❌ deleteSettingsSnapshot() - Use updateSettings() with defaults if needed
 * ❌ cleanupOldSettingsSnapshots() - No old snapshots
 * ❌ verifySettingsSnapshotIntegrity() - No integrity checks needed
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ALL ISSUES SOLVED:
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ✅ Issue #1: ACID Transactions
 *    - PostgreSQL UPSERT is atomic (single operation)
 *
 * ✅ Issue #2: Counter Before Operation
 *    - N/A (no version counters in new schema)
 *
 * ✅ Issue #3: Multi-Step Not Atomic
 *    - Single UPSERT operation (no multi-step workflow)
 *
 * ✅ Issue #4: Export Not Atomic
 *    - N/A (single row per user, no batch export)
 *
 * ✅ Issue #5: Lock Timeout Race
 *    - PostgreSQL handles concurrent UPSERTs correctly
 *    - Row-level locking prevents data corruption
 *
 * ✅ Issue #6: Bulk Partial Failure
 *    - N/A (single operation per user)
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */